import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sidebar } from "../components/Sidebar";
import { TranscriptPanel } from "../components/TranscriptPanel";
import { MicControls } from "../components/MicControls";
import { TextInputBox } from "../components/TextInputBox";
import { SettingsDialog } from "../components/SettingsDialog";
import { NotesPanel } from "../components/NotesPanel";
import { StudioPanel } from "../components/StudioPanel";
import { FilesPanel } from "../components/FilesPanel";
import {
  auth,
  sessionsApi,
  buildWsUrl,
  clearToken,
} from "../lib/api";
import { PcmPlayer, startMicCapture } from "../lib/audio";
import { createWakeWordListener, wakeWordSupported } from "../lib/wakeWord";
import {
  detectImageIntent,
  detectVideoIntent,
  detectSaveNoteIntent,
  detectMemoryIntent,
} from "../lib/voiceIntents";
import { generationsApi, notesApi } from "../lib/api";

export default function ChatPage() {
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = window.localStorage.getItem("glitch_sidebar_open");
    return saved === "1";
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(
    typeof window === "undefined" || window.innerHeight >= 720
  );
  const [active, setActive] = useState(false); // mic active
  const [connectionState, setConnectionState] = useState("disconnected");
  const [orbState, setOrbState] = useState("idle");
  const [liveUser, setLiveUser] = useState("");
  const [liveAssistant, setLiveAssistant] = useState("");
  const [wakeWordEnabled, setWakeWordEnabled] = useState(false);
  const [wakeWordActive, setWakeWordActive] = useState(false);
  const [volume, setVolume] = useState(() => {
    if (typeof window === "undefined") return 0.8;
    const saved = parseFloat(window.localStorage.getItem("glitch_volume"));
    return isNaN(saved) ? 0.8 : Math.max(0, Math.min(1, saved));
  });
  const wakeSupported = wakeWordSupported();

  const wsRef = useRef(null);
  const stopMicRef = useRef(null);
  const playerRef = useRef(null);
  const levelIntervalRef = useRef(null);
  const wakeRef = useRef(null);
  const activeRef = useRef(false);
  const intentionalCloseRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const lastAssistantRef = useRef("");
  const lastIntentTranscriptRef = useRef("");
  const messagesRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    messagesRef.current = messages;
    // Track the latest assistant message for "save that" intent
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        lastAssistantRef.current = messages[i].text;
        break;
      }
    }
  }, [messages]);

  // --- Auth guard + initial load
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const meRes = await auth.me();
        if (!mounted) return;
        setUser(meRes.user);
        const s = await sessionsApi.list();
        if (!mounted) return;
        setSessions(s.sessions);
        if (s.sessions.length > 0) {
          setActiveSessionId(s.sessions[0].id);
        } else {
          const created = await sessionsApi.create();
          setSessions([created.session]);
          setActiveSessionId(created.session.id);
        }
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  // --- Load messages when session changes
  useEffect(() => {
    if (!activeSessionId) return;
    (async () => {
      try {
        const res = await sessionsApi.messages(activeSessionId);
        setMessages(res.messages || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [activeSessionId]);

  // --- Player playback state → orb state
  useEffect(() => {
    // ElevenLabs Conversational AI streams PCM16 at 16kHz. Using any other
    // rate here plays audio at the wrong pitch/speed (chipmunk effect).
    if (!playerRef.current) {
      playerRef.current = new PcmPlayer(16000);
      playerRef.current.setVolume(volume);
    }
    return () => {
      playerRef.current?.close();
      playerRef.current = null;
    };
  }, []);

  // Keep the audio graph's gain in sync with the volume slider.
  useEffect(() => {
    playerRef.current?.setVolume(volume);
    try {
      window.localStorage.setItem("glitch_volume", String(volume));
    } catch (_) {
      /* ignore */
    }
  }, [volume]);

  // --- Cleanup on unmount
  useEffect(() => {
    return () => {
      stopConversation();
      wakeRef.current?.stop();
    };
  }, []);

  // Refresh transcript when generations are attached from Studio
  useEffect(() => {
    const onRefresh = async () => {
      if (!activeSessionId) return;
      try {
        const res = await sessionsApi.messages(activeSessionId);
        setMessages(res.messages || []);
      } catch (_) {
        /* ignore */
      }
    };
    window.addEventListener("glitch:transcript-refresh", onRefresh);
    return () =>
      window.removeEventListener("glitch:transcript-refresh", onRefresh);
  }, [activeSessionId]);

  // --- Wake word listener: run only when enabled AND no active conversation
  useEffect(() => {
    // Always tear down first
    wakeRef.current?.stop();
    wakeRef.current = null;
    setWakeWordActive(false);

    if (!wakeWordEnabled || !wakeSupported) return;
    if (active) return; // don't listen while in a call

    const listener = createWakeWordListener({
      onDetected: () => {
        if (!activeRef.current) {
          setWakeWordActive(false);
          toast.success("Hey Glitch — I'm here.");
          startConversation();
        }
      },
      onError: (err) => {
        // Permission denied → auto-disable
        if (err === "not-allowed" || err === "service-not-allowed") {
          setWakeWordEnabled(false);
          setWakeWordActive(false);
          toast.error("Microphone permission needed for wake word");
        }
      },
    });
    listener.start();
    wakeRef.current = listener;
    setWakeWordActive(true);
    return () => {
      listener.stop();
    };
  }, [wakeWordEnabled, wakeSupported, active]);

  const refreshSessions = useCallback(async () => {
    try {
      const s = await sessionsApi.list();
      setSessions(s.sessions);
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Voice-triggered actions: image/video generation, save note.
  const runVoiceIntents = useCallback(
    async (text) => {
      if (!text || text.trim().length < 5) return;

      // Save note intent
      const save = detectSaveNoteIntent(text);
      if (save.matched) {
        const noteText = (lastAssistantRef.current || "").trim();
        if (!noteText) {
          toast.info("Nothing to save yet — chat with Glitch first.");
        } else {
          try {
            await notesApi.create({
              project: save.project,
              text: noteText,
              source_session_id: activeSessionId,
            });
            toast.success(`Saved to ${save.project}`);
          } catch (e) {
            toast.error("Note save failed");
          }
        }
        return;
      }

      // Memory intent: "remember that ...", "save this to memory", etc.
      const mem = detectMemoryIntent(text);
      if (mem.matched) {
        let content = mem.content;
        if (!content && mem.useLastAssistant) {
          content = (lastAssistantRef.current || "").trim();
        }
        if (!content || content.length < 3) {
          toast.info("What should I remember? Try 'Remember that my dog's name is Rex.'");
          return;
        }
        try {
          const res = await auth.appendMemory(content);
          if (res.duplicate) {
            toast.info("Already in memory", { description: content.slice(0, 80) });
          } else {
            toast.success("Saved to memory", {
              description:
                content.slice(0, 90) +
                " — Glitch will remember this from your next chat.",
            });
          }
          // Update local user state so Settings dialog reflects it immediately
          setUser((u) => (u ? { ...u, memory: res.memory } : u));
        } catch (e) {
          toast.error("Memory save failed");
        }
        return;
      }

      // Image intent
      const img = detectImageIntent(text);
      if (img.matched) {
        toast.info(`Drawing "${img.prompt}"…`, { duration: 2500 });
        try {
          const savedImageModel =
            (typeof window !== "undefined" &&
              window.localStorage.getItem("glitch_image_model")) ||
            undefined;
          const savedImageAspect =
            (typeof window !== "undefined" &&
              window.localStorage.getItem("glitch_image_aspect")) ||
            undefined;
          const res = await generationsApi.createImage(
            img.prompt,
            savedImageModel,
            false,
            undefined,
            savedImageAspect
          );
          toast.success("Image ready", {
            description: img.prompt.slice(0, 80),
          });
          window.dispatchEvent(
            new CustomEvent("glitch:generation", { detail: res.generation })
          );
          if (activeSessionId && res.generation.file_url) {
            try {
              await sessionsApi.appendMessage(activeSessionId, {
                role: "assistant",
                text: `Here you go — ${img.prompt}`,
                attachment: {
                  kind: "image",
                  file_url: res.generation.file_url,
                  prompt: img.prompt,
                  generation_id: res.generation.id,
                },
              });
              const refreshed = await sessionsApi.messages(activeSessionId);
              setMessages(refreshed.messages || []);
            } catch (_) {
              /* ignore */
            }
          }
        } catch (e) {
          toast.error(
            e?.response?.data?.detail || e.message || "Image failed"
          );
        }
        return;
      }

      // Video intent
      const vid = detectVideoIntent(text);
      if (vid.matched) {
        toast.info(`Rendering a ${vid.duration}s clip…`, {
          description: vid.prompt.slice(0, 80),
          duration: 4000,
        });
        try {
          const savedModel =
            (typeof window !== "undefined" &&
              window.localStorage.getItem("glitch_video_model")) ||
            undefined;
          const savedVideoAspect =
            (typeof window !== "undefined" &&
              window.localStorage.getItem("glitch_video_aspect")) ||
            undefined;
          const res = await generationsApi.createVideo(
            vid.prompt,
            vid.duration,
            savedModel,
            false,
            undefined,
            savedVideoAspect
          );
          window.dispatchEvent(
            new CustomEvent("glitch:generation", { detail: res.generation })
          );
          // Attach a pending placeholder immediately so the user sees it in the transcript
          if (activeSessionId) {
            try {
              await sessionsApi.appendMessage(activeSessionId, {
                role: "assistant",
                text: `Rendering a ${vid.duration}s clip of ${vid.prompt}…`,
                attachment: {
                  kind: "video",
                  file_url: null,
                  prompt: vid.prompt,
                  generation_id: res.generation.id,
                  status: "pending",
                },
              });
              const refreshed = await sessionsApi.messages(activeSessionId);
              setMessages(refreshed.messages || []);
            } catch (_) {
              /* ignore */
            }
          }
          // Poll status → when done, attach final video message
          (async () => {
            const genId = res.generation.id;
            for (let i = 0; i < 30; i++) {
              await new Promise((r) => setTimeout(r, 6000));
              try {
                const s = await generationsApi.videoStatus(genId);
                const g = s.generation;
                if (g.status === "done" && g.file_url) {
                  if (activeSessionId) {
                    await sessionsApi.appendMessage(activeSessionId, {
                      role: "assistant",
                      text: `Here's your ${vid.duration}s clip.`,
                      attachment: {
                        kind: "video",
                        file_url: g.file_url,
                        prompt: vid.prompt,
                        generation_id: genId,
                      },
                    });
                    const refreshed = await sessionsApi.messages(
                      activeSessionId
                    );
                    setMessages(refreshed.messages || []);
                  }
                  window.dispatchEvent(
                    new CustomEvent("glitch:generation", { detail: g })
                  );
                  toast.success("Video ready");
                  return;
                }
                if (g.status === "failed") {
                  toast.error("Video failed: " + (g.error || "unknown"));
                  return;
                }
              } catch (_) {
                /* keep polling */
              }
            }
          })();
        } catch (e) {
          toast.error(
            e?.response?.data?.detail || e.message || "Video failed"
          );
        }
      }
    },
    [activeSessionId]
  );

  // Test hook: allow injecting a user transcript from outside (e.g. e2e tests).
  // Uses a ref indirection so the hook is order-independent from runVoiceIntents.
  const runVoiceIntentsRef = useRef(runVoiceIntents);
  useEffect(() => {
    runVoiceIntentsRef.current = runVoiceIntents;
  }, [runVoiceIntents]);
  useEffect(() => {
    window.__glitch_test_transcript = (text) => runVoiceIntentsRef.current?.(text);
    return () => {
      delete window.__glitch_test_transcript;
    };
  }, []);


  const startConversation = async () => {
    if (!activeSessionId) return;
    if (connectionState === "connecting" || connectionState === "connected")
      return;
    if (intentionalCloseRef.current) {
      // Race: a stop was requested while a reconnect timer was queued
      intentionalCloseRef.current = false;
      return;
    }
    intentionalCloseRef.current = false;
    setConnectionState("connecting");
    setLiveUser("");
    setLiveAssistant("");

    try {
      const ws = new WebSocket(buildWsUrl(activeSessionId));
      wsRef.current = ws;

      ws.onopen = () => {
        // wait for 'ready' from backend
      };

      ws.onmessage = async (evt) => {
        let msg;
        try {
          msg = JSON.parse(evt.data);
        } catch (_) {
          return;
        }
        switch (msg.type) {
          case "ready": {
            reconnectAttemptsRef.current = 0;
            setConnectionState("connected");
            setActive(true);
            setOrbState("listening");
            // Start mic (only if not already running from a previous session)
            if (!stopMicRef.current) {
              const stop = await startMicCapture(
                (b64) => {
                  const w = wsRef.current;
                  if (w && w.readyState === WebSocket.OPEN) {
                    w.send(JSON.stringify({ type: "audio", data: b64 }));
                  }
                },
                null
              );
              stopMicRef.current = stop;
            }
            // Playback level watcher for speaking animation
            if (!levelIntervalRef.current) {
              levelIntervalRef.current = setInterval(() => {
                const p = playerRef.current;
                if (p && p.isPlaying()) {
                  setOrbState("speaking");
                } else {
                  setOrbState((s) => (s === "speaking" ? "listening" : s));
                }
              }, 120);
            }
            break;
          }
          case "audio": {
            if (playerRef.current && msg.data) {
              playerRef.current.enqueueBase64(msg.data);
              setOrbState("speaking");
            }
            break;
          }
          case "interrupted": {
            playerRef.current?.flush();
            setLiveAssistant("");
            setOrbState("listening");
            break;
          }
          case "user_transcript": {
            setLiveUser(msg.full || "");
            setOrbState("listening");
            const text = msg.full || msg.text || "";
            // Only fire an intent once per new utterance (backend usually
            // sends the same full transcript each chunk).
            if (text && text !== lastIntentTranscriptRef.current) {
              lastIntentTranscriptRef.current = text;
              runVoiceIntents(text);
            }
            break;
          }
          case "assistant_transcript": {
            setLiveAssistant(msg.full || "");
            setOrbState((s) => (s === "listening" ? "thinking" : s));
            break;
          }
          case "turn_complete": {
            setLiveUser("");
            setLiveAssistant("");
            lastIntentTranscriptRef.current = "";
            try {
              const res = await sessionsApi.messages(activeSessionId);
              setMessages(res.messages || []);
              refreshSessions();
            } catch (_) {
              /* ignore */
            }
            setOrbState("listening");
            break;
          }
          case "session_renamed": {
            refreshSessions();
            break;
          }
          case "error": {
            toast.error(msg.message || "Voice error");
            intentionalCloseRef.current = true;
            setConnectionState("error");
            stopConversation();
            break;
          }
          default:
            break;
        }
      };

      ws.onerror = () => {
        // Do not surface toast here; onclose handles the recovery path.
      };

      ws.onclose = () => {
        setConnectionState("disconnected");
        // Only reconnect if the user did NOT ask to stop
        if (!intentionalCloseRef.current && activeRef.current) {
          const attempt = reconnectAttemptsRef.current + 1;
          reconnectAttemptsRef.current = attempt;
          if (attempt <= 4) {
            const delay = Math.min(1200 * attempt, 4000);
            if (attempt === 1) {
              toast.info("Reconnecting…", { duration: 1500 });
            }
            setOrbState("thinking");
            reconnectTimerRef.current = setTimeout(() => {
              startConversation();
            }, delay);
            return; // keep mic capture alive across reconnect
          }
          toast.error("Voice connection lost. Tap the mic to retry.");
        }
        // Real stop
        setActive(false);
        setOrbState("idle");
        stopMicRef.current?.();
        stopMicRef.current = null;
        if (levelIntervalRef.current) {
          clearInterval(levelIntervalRef.current);
          levelIntervalRef.current = null;
        }
      };
    } catch (e) {
      toast.error(e.message || "Could not start voice");
      setConnectionState("error");
    }
  };

  const stopConversation = () => {
    intentionalCloseRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    try {
      wsRef.current?.close();
    } catch (_) {
      /* ignore */
    }
    wsRef.current = null;
    stopMicRef.current?.();
    stopMicRef.current = null;
    playerRef.current?.flush();
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current);
      levelIntervalRef.current = null;
    }
    setActive(false);
    setOrbState("idle");
    setConnectionState("disconnected");
    setLiveUser("");
    setLiveAssistant("");
  };

  const toggleMic = () => {
    if (active) stopConversation();
    else startConversation();
  };

  const onNewSession = async () => {
    stopConversation();
    try {
      const res = await sessionsApi.create();
      setSessions((s) => [res.session, ...s]);
      setActiveSessionId(res.session.id);
      setMessages([]);
      toast.success("Fresh start");
    } catch (e) {
      toast.error("Could not start new session");
    }
  };

  const onSelectSession = (id) => {
    if (id === activeSessionId) return;
    stopConversation();
    setActiveSessionId(id);
  };

  const onRenameSession = async (id, title) => {
    const res = await sessionsApi.rename(id, title);
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? res.session : s))
    );
  };

  const onDeleteSession = async (id) => {
    await sessionsApi.remove(id);
    const remaining = sessions.filter((s) => s.id !== id);
    setSessions(remaining);
    if (id === activeSessionId) {
      stopConversation();
      if (remaining.length > 0) {
        setActiveSessionId(remaining[0].id);
      } else {
        const created = await sessionsApi.create();
        setSessions([created.session]);
        setActiveSessionId(created.session.id);
        setMessages([]);
      }
    }
    toast.success("Deleted");
  };

  const onLogout = async () => {
    stopConversation();
    try {
      await auth.logout();
    } catch (_) {
      /* ignore */
    }
    clearToken();
    navigate("/login", { replace: true });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white/50 text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div
      data-testid="chat-page"
      className="relative w-full flex flex-col h-[100dvh] overflow-hidden"
    >
      <Sidebar
        open={sidebarOpen}
        onToggle={() => {
          setSidebarOpen((v) => {
            const next = !v;
            try {
              window.localStorage.setItem(
                "glitch_sidebar_open",
                next ? "1" : "0"
              );
            } catch (_) {
              /* ignore */
            }
            return next;
          });
        }}
        sessions={sessions}
        activeSessionId={activeSessionId}
        onSelectSession={onSelectSession}
        onNewSession={onNewSession}
        onRenameSession={onRenameSession}
        onDeleteSession={onDeleteSession}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenNotes={() => setNotesOpen(true)}
        onOpenStudio={() => setStudioOpen(true)}
        onOpenFiles={() => setFilesOpen(true)}
        onLogout={onLogout}
        userName={user.name}
        assistantName={user.assistant_name}
      />

      {/* Content column – shifts right when sidebar is open on desktop.
          On mobile the sidebar toggle floats over the header, so no left pad
          is needed on the whole column (that was pushing the mic off-center). */}
      <div
        className={`flex-1 min-h-0 flex flex-col transition-[padding] duration-300 ${
          sidebarOpen ? "md:pl-80" : "md:pl-16"
        }`}
      >
        {/* Header – padded so the floating sidebar toggle doesn't overlap the title on mobile */}
        <div className="shrink-0 px-5 pt-5 pb-2 pl-16 md:pl-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-300/60">
            Current session
          </div>
          <div
            data-testid="active-session-title"
            className="font-display text-lg text-white/90 truncate max-w-full"
          >
            {sessions.find((s) => s.id === activeSessionId)?.title || "—"}
          </div>
        </div>

        {/* Transcript wrapper – now anchored at the top (orb removed) */}
        <div className="flex-1 min-h-0 flex flex-col justify-start pt-2">
          <TranscriptPanel
            messages={messages}
            liveUser={liveUser}
            liveAssistant={liveAssistant}
            open={transcriptOpen}
            onToggle={() => setTranscriptOpen((v) => !v)}
            sessionId={activeSessionId}
          />
        </div>

        {/* Text / paste box – silent text-only chat (no voice reply) */}
        <TextInputBox
          onSend={async (payload) => {
            // payload is either a plain string OR { text, attachment: {id, filename, ...} }
            const text = typeof payload === "string" ? payload : payload.text || "";
            const attachment = typeof payload === "string" ? null : payload.attachment;
            const displayText =
              text.trim() ||
              (attachment ? `[Attached ${attachment.filename}]` : "");
            const localUserId = `local-${Date.now()}`;
            setMessages((prev) => [
              ...prev,
              {
                id: localUserId,
                role: "user",
                text: displayText,
                created_at: new Date().toISOString(),
                attachment: attachment
                  ? {
                      kind: "file",
                      file_id: attachment.id,
                      filename: attachment.filename,
                      content_type: attachment.content_type,
                      size: attachment.size,
                    }
                  : null,
              },
            ]);
            try {
              const res = await sessionsApi.chat(
                activeSessionId,
                text,
                attachment
              );
              // Replace the optimistic user turn with the real one, and append
              // the assistant reply.
              setMessages((prev) => {
                const withoutLocal = prev.filter((m) => m.id !== localUserId);
                return [
                  ...withoutLocal,
                  res.user_message,
                  res.assistant_message,
                ];
              });
              return true;
            } catch (e) {
              // Roll back the optimistic user message on failure
              setMessages((prev) => prev.filter((m) => m.id !== localUserId));
              toast.error(
                e?.response?.data?.detail || "Text chat failed"
              );
              return false;
            }
          }}
        />

        {/* Mic controls */}
        <MicControls
          active={active}
          onToggle={toggleMic}
          connectionState={connectionState}
          wakeWordEnabled={wakeWordEnabled}
          onWakeWordChange={(v) => {
            setWakeWordEnabled(v);
            if (v) {
              toast.success('Listening for "Hey Glitch"', {
                description: "Wake word is on — say it anytime.",
              });
            } else {
              toast.info("Wake word off");
            }
          }}
          wakeWordSupported={wakeSupported}
          wakeWordActive={wakeWordActive}
          orbState={orbState}
          volume={volume}
          onVolumeChange={setVolume}
        />
      </div>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        user={user}
        onSaved={setUser}
      />

      <NotesPanel
        open={notesOpen}
        onOpenChange={setNotesOpen}
        sourceSessionId={activeSessionId}
      />

      <StudioPanel
        open={studioOpen}
        onOpenChange={setStudioOpen}
        activeSessionId={activeSessionId}
        user={user}
      />

      <FilesPanel
        open={filesOpen}
        onOpenChange={setFilesOpen}
        activeSessionId={activeSessionId}
      />
    </div>
  );
}
